import React from "react";
import { useSelector } from "react-redux";
import { useGetUserBookBasedBadgesQuery } from "../../../redux-toolkit/features/my-activity/myActivityApiSlice";
import BaseJourney from "./BaseJourney";

const BooksJourney = ({ currentProgress }) => {
  const { user } = useSelector((state) => state.auth);
  const userId = user?._id;

  const {
    data: bookBadgesData,
    isLoading,
  } = useGetUserBookBasedBadgesQuery(userId);

  // Get badges from API data
  const earnedBadges = bookBadgesData?.data?.earnedBadges || [];

  // Sort badges by valueThreshold to ensure correct order
  const sortedBadges = [...earnedBadges].sort(
    (a, b) => a.valueThreshold - b.valueThreshold,
  );

  // Calculate progress based on earned badges
  const earnedBadgesCount = sortedBadges.filter(
    (badge) => badge.isEarned,
  ).length;
  const totalBadges = sortedBadges.length;
  const maxProgress = totalBadges || 7; // Use total badges as max progress
  const calculatedProgress = totalBadges > 0 ? earnedBadgesCount : 0;


  // Create timeline markers from badge thresholds
  const timelineMarkers =
    sortedBadges.length > 0
      ? sortedBadges.map((badge) => badge.valueThreshold.toString())
      : ["1", "2", "3", "4", "5", "6", "7"]; // Fallback markers

  // Transform API data to match our badge structure
  const badgeData =
    sortedBadges.length > 0
      ? sortedBadges.map((badge) => ({
          id: badge._id || badge.valueThreshold,
          image: badge.badgeImage || "",
          title: (
            badge.badgeName || `${badge.valueThreshold} Books`
          ).toUpperCase(),
          description: getBadgeDescription(
            badge.badgeName,
            badge.valueThreshold,
          ),
          isAchieved: Boolean(badge.isEarned),
        }))
      : []; // Empty array if no API data

  // Helper function to generate badge descriptions
  function getBadgeDescription(badgeName, threshold) {
    // Use the badge name to create descriptions, fallback to threshold-based descriptions
    if (badgeName) {
      const nameMap = {
        "Red Deer Book Completed": "Your first adventure into the wild",
        "Goliath Beetle Book Completed": "Discovering the world of insects",
        "Silvertip Bear Book Completed": "Exploring the wilderness",
        "Laughing Falcon Book Completed": "Soaring through avian knowledge",
        "Diamond Python Book Completed": "Slithering into reptilian wisdom",
        "Komodo Dragon Book Completed": "Mastering the apex predators",
        "Blue Whale Book Completed": "Diving deep into marine mysteries",
      };
      return (
        nameMap[badgeName] ||
        `Completed the ${badgeName.replace(" Book Completed", "")} journey`
      );
    }

    // Fallback descriptions based on threshold
    switch (threshold) {
      case 1:
        return "Your first literary conquest";
      case 2:
        return "Building momentum in reading";
      case 3:
        return "Expanding your knowledge library";
      case 4:
        return "Becoming a dedicated reader";
      case 5:
        return "Halfway to book mastery";
      case 6:
        return "Advanced literary explorer";
      case 7:
        return "Almost a bookworm master";
      default:
        return `Complete ${threshold} books to earn this badge`;
    }
  }

  // Show loading state if badges are still loading
  if (isLoading) {
    return (
      <div className="journey-container">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
            color: "#fff",
            fontSize: "1rem",
          }}
        >
          Loading book badges...
        </div>
      </div>
    );
  }

  return (
    <BaseJourney
      currentProgress={calculatedProgress}
      maxProgress={maxProgress}
      badgeData={badgeData}
      timelineMarkers={timelineMarkers}
      progressColor="#9C27B0"
      actualScore={currentProgress} // Pass the actual books count for comparison with valueThreshold
    />
  );
};

export default BooksJourney;
